import { z } from "zod";

import { Decimal } from "~/utils/types/decimal";

export const requiredUuid = z.preprocess(
  val => (val == null || (typeof val === "string" && val.length === 0) ? undefined : val),
  z.string().uuid(),
);

/**
 * Collapses the empty-string form sentinel to null so an explicit clear of a nullable
 * relation persists (Prisma disconnects on null, but omits the field on undefined), while
 * still allowing the field to be omitted entirely. `requiredUuid.optional()` does not work
 * here: preprocess runs first and turns "" / null into undefined, then `z.string().uuid()`
 * rejects them as Required.
 */
export const optionalUuid = z.preprocess(
  val => (typeof val === "string" && val.length === 0 ? null : val),
  z.string().uuid().nullable().optional(),
);

/**
 * Normalizes the empty-string form sentinel to null while requiring a UUID or null.
 * Zod preprocess widens the input type to unknown; runtime validation rejects all
 * other values, and callers must opt into accepting an omitted field.
 */
export const nullableUuid = z.preprocess(value => (value === "" ? null : value), z.string().uuid().nullable());

export const stringEmptyForcedNull = z
  .string()
  .nullable()
  .transform(value => ((value ?? "").length === 0 ? null : value));

export const uuid = () => z.preprocess(v => (v == null || (typeof v === "string" && v.length === 0) ? undefined : v), z.string().uuid());

export const PK = z.object({
  id: requiredUuid,
});

export const isUuid = (value: unknown) => uuid().safeParse(value).success;

type DecimalProps = {
  positive?: boolean;
  nonnegative?: boolean;
};
export const decimal = (props?: DecimalProps) =>
  z
    .instanceof(Decimal)
    .or(z.string())
    .or(z.number())
    .refine(value => {
      try {
        return new Decimal(value);
      } catch {
        return false;
      }
    })
    .transform(value => new Decimal(value))
    .refine(value => props?.nonnegative !== true || value.gte(0), "Number must be greater than or equal to 0")
    .refine(value => props?.positive !== true || value.gt(0), "Number must be greater than 0");

export const email = () =>
  z
    .string()
    .transform(s => s.replaceAll(/\s+/g, ""))
    .pipe(z.string().email());
